import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import {
  Grid,
} from '@mui/material';
import { USStates, CAProvinces, COUNTRIES } from 'utils/constant.js';
import InputField from 'components/Input-Field/input-field';
import SelectField from 'components/Select-Field/select-field';
import FormElements from 'components/FormElements';

const DriverForm = ({
  action,
  driver,
  saveFormHandler,
  updateFormHandler,
  exposeSubmit,
}) => {

  const DriverSchema = Yup.object().shape({
    driverType: Yup.string().required('Select Driver Type'),
    fullName: Yup.string().min(2).max(100).required('Driver Name is required.'),
    phone: Yup.string().max(15).required('Phone number is required'),
    licenseNumber: Yup.string().min(2).max(20).required('License Number is required.'),
    licenseExpiryDate: Yup.string().required('License Expiry Date is required.'),
    licenseState: Yup.string().min(2).max(25).required('License State Name is required.'),
    insuredName: Yup.string().min(2).max(100).required('Insured Name is required.'),
    policyNumber: Yup.string().min(2).max(20).required('Policy Number is required.'),
    policyExpiryDate: Yup.string().required('Policy Expiry Date is required.'),
    city: Yup.string().min(2).max(50).required('City Name is required.'),
    state: Yup.string().min(2).max(25).required('State Name is required.'),
    country: Yup.string().max(25).required('Country Name is required.'),
  });

  const formatPhoneNumber = (e) => {
    if (e.target.name === 'phone') {
      var x = e.target.value
        .replace(/\D/g, '')
        .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
      e.target.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
      return e.target.value;
    }
  };

  return (
    <Formik
      initialValues={{...driver}}
      enableReinitialize={true}
      validationSchema={DriverSchema}
      onSubmit={action === 'Save' ? saveFormHandler : updateFormHandler}
    >
      {(formik) => {
        // expose the submitForm to parent via ref
        useEffect(() => {
          exposeSubmit?.(formik.submitForm);
        }, [formik.submitForm]);
        return (
          <Form>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12 }}>
                <FormElements.SectionTitle title='Personal Information' />
              </Grid>
              <Grid size={{ xs: 4, sm: 3 }}>
                <SelectField
                  fullWidth={true}
                  name='driverType'
                  label={'Select Driver Type'}
                  variant='outlined'
                  value={formik.values.driverType}
                  error={Boolean(formik.touched.driverType && formik.errors.driverType)}
                  helperText={formik.touched.driverType && formik.errors.driverType}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  options={['EMPLOYEE', 'OWNER_OPERATOR', 'CONTRACT']}
                  size='small'
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 3 }}>
                <InputField
                  label="Driver Name"
                  name="fullName"
                  value={formik.values.fullName}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.fullName && formik.errors.fullName)}
                  helperText={formik.touched.fullName && formik.errors.fullName}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 3 }}>
                <InputField
                  label="Driver Phone Number"
                  name="phone"
                  type="tel"
                  value={formik.values.phone}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.phone && formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  onBlur={formik.handleBlur}
                  onChange={e => {
                    const phone = formatPhoneNumber(e);
                    formik.setFieldValue('phone', phone);
                  }}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 3 }}>
                <InputField
                  label="Base Rate"
                  name="baseRate"
                  type="number"
                  value={formik.values.baseRate}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.baseRate && formik.errors.baseRate)}
                  helperText={formik.touched.baseRate && formik.errors.baseRate}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>              
              <Grid size={{ xs: 4, sm: 4 }}>
                <InputField
                  label="Driver Email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.email && formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <SelectField
                  label="Select Country"
                  name="country"
                  value={formik.values.country}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.country && formik.errors.country)}
                  helperText={formik.touched.country && formik.errors.country}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  options={COUNTRIES}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <InputField
                  label="City"
                  name="city"
                  value={formik.values.city}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.city && formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <SelectField
                  label="Select State/Province"
                  name="state"
                  select
                  value={formik.values.state}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.state && formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  options={formik.values['country'] === 'USA' ? USStates : CAProvinces}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}></Grid>
              <Grid size={{ xs: 4, sm: 4 }}></Grid>
              <Grid size={{ xs: 12 }}>
                <FormElements.SectionTitle title='License Information' />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <InputField
                  label="License Number"
                  name="licenseNumber"
                  value={formik.values.licenseNumber}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.licenseNumber && formik.errors.licenseNumber)}
                  helperText={formik.touched.licenseNumber && formik.errors.licenseNumber}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <InputField
                  label="Expiry Date"
                  name="licenseExpiryDate"
                  value={formik.values.licenseExpiryDate}
                  type="date"
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.licenseExpiryDate && formik.errors.licenseExpiryDate)}
                  helperText={formik.touched.licenseExpiryDate && formik.errors.licenseExpiryDate}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <SelectField
                  label="License State/Province"
                  name="licenseState"
                  select
                  value={formik.values.licenseState}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.licenseState && formik.errors.licenseState)}
                  helperText={formik.touched.licenseState && formik.errors.licenseState}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  options={formik.values['country'] === 'USA' ? USStates : CAProvinces}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormElements.SectionTitle title='Insurance Information' />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <InputField
                  label="Insurance Provider"
                  name="insuranceCarrier"
                  value={formik.values.insuranceCarrier}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.insuranceCarrier && formik.errors.insuranceCarrier)}
                  helperText={formik.touched.insuranceCarrier && formik.errors.insuranceCarrier}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <InputField
                  label="Insured Name"
                  name="insuredName"
                  value={formik.values.insuredName}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.insuredName && formik.errors.insuredName)}
                  helperText={formik.touched.insuredName && formik.errors.insuredName}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <InputField
                  label="Policy Number"
                  name="policyNumber"
                  value={formik.values.policyNumber}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.policyNumber && formik.errors.policyNumber)}
                  helperText={formik.touched.policyNumber && formik.errors.policyNumber}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <InputField
                  label="Expiry Date"
                  name="policyExpiryDate"
                  value={formik.values.policyExpiryDate}
                  type="date"
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.policyExpiryDate && formik.errors.policyExpiryDate)}
                  helperText={formik.touched.policyExpiryDate && formik.errors.policyExpiryDate}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 4, sm: 4 }}>
                <InputField
                  label="Certificate Holder Name"
                  name="certificateHolder"
                  value={formik.values.certificateHolder}
                  variant="outlined"
                  fullWidth
                  error={Boolean(formik.touched.certificateHolder && formik.errors.certificateHolder)}
                  helperText={formik.touched.certificateHolder && formik.errors.certificateHolder}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      }
    </Formik>
  );
};
export default DriverForm;